import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import { versionDe, type Calendario, type Cierre, type DiaEspecial, type Festivo, type NuevoDiaEspecial, type Reservable } from '../../api/ops/disponibilidad';
import { ErrorApi } from '../../api/problema';
import type { MostrarAviso } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deslizador } from '../../componentes/Deslizador';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import {
    ajustarRango,
    anioDe,
    baldosa,
    desdeEscrito,
    DESLIZADOR,
    diaDeLaSemana,
    DIAS,
    DURACION_MAXIMA,
    enZona,
    erroresDeSemana,
    fechaCorta,
    fechaLarga,
    hastaEscrito,
    rangoDeTramo,
    sumarDias,
    textoDuracion,
    tramoDeRango,
    tramoNuevo,
    type Rango,
    type Semana,
} from '../../logica/horario';
import type { Cambio } from './cambios';
import { ESTADO_CIERRE, fechasDe, lineaDia, MARCAS, MOTIVO_CIERRE, NOMBRE_TIPO, TEXTO_CONFLICTO_DIA, TIPOS_DIA } from './textos';

/**
 * 3.2 «Excepciones y festivos», debajo del horario en la misma página (`?apartado=excepciones` baja hasta aquí):
 * - Los días especiales por fecha de los próximos 3 meses (`listExceptions` pide 92 días como mucho) y «Ver más adelante».
 *   En una sede salen también los del negocio, que se cambian en «Todo el negocio»; un horario especial del negocio no
 *   cambia lo que tiene horario propio (G249).
 * - «Añadir día especial» (RV Panel): Cerrado · Horario especial · Festivo, Desde y Hasta, tramos con el mismo
 *   deslizador y motivo. Guardar y quitar pasan por la vista previa (G250).
 * - «Festivos que puedes añadir» (G251): nacionales y de la comunidad de la sede, del catálogo; ninguno se aplica solo y
 *   «Añadir» crea `kind: holiday` con su `holiday_code`. Los locales, a mano con el tipo «Festivo».
 * - Cierres y retenes, plegados y solo de consulta (se hacen en Cierre de recursos y en Sala).
 */
interface Props {
    ops: Operaciones;
    negocio: string;
    /** El calendario elegido (null: una sede que aún no tiene el suyo, G248). */
    calendario: Calendario | null;
    calNegocio: Calendario | null;
    esSede: boolean;
    /** Nombre de la sede elegida. */
    nombreSede: string;
    sedeConPropio: boolean;
    /** Las sedes con horario propio (vistas desde «Todo el negocio», G249). */
    sedesConPropio: readonly string[];
    pais: string;
    nombrePais: string;
    region: string | null;
    /** «Todo el negocio» con sedes de comunidades distintas: solo salen los festivos nacionales (G372). */
    comunidadesMezcladas?: boolean;
    nombreRegion: string | null;
    zona: string;
    hoy: string;
    editable: boolean;
    /** La semana de este calendario, para avisar de que un horario especial abre un día que se cierra. */
    semana: Semana;
    porDefecto: Rango;
    movil: boolean;
    reservables: readonly Reservable[];
    cierres: readonly Cierre[];
    calculando: boolean;
    pedir: (c: Cambio) => Promise<void>;
    asegurarCalendario: () => Promise<Calendario | null>;
    avisar: MostrarAviso;
}

const VENTANA_DIAS = 92;

interface Borrador {
    tipo: DiaEspecial['kind'];
    desde: string;
    hasta: string;
    tramos: Rango[];
    motivo: string;
}

type Abierto = { dia: DiaEspecial | null; b: Borrador; intentado: boolean; errores: Record<string, string> };

async function todasLasPaginas(ops: Operaciones, negocio: string, calendarId: string, desde: string, hasta: string): Promise<DiaEspecial[]> {
    const dias: DiaEspecial[] = [];
    let cursor: string | null = null;
    do {
        const p = await ops.listExceptions(negocio, calendarId, desde, hasta, { limit: 100, cursor });
        dias.push(...p.data);
        cursor = p.next_cursor;
    } while (cursor !== null);
    return dias;
}

async function diasEn(ops: Operaciones, negocio: string, calendarId: string, hoy: string, ventanas: number): Promise<DiaEspecial[]> {
    const partes = await Promise.all(
        Array.from({ length: ventanas }, (_, i) => todasLasPaginas(ops, negocio, calendarId, sumarDias(hoy, i * VENTANA_DIAS), sumarDias(hoy, (i + 1) * VENTANA_DIAS - 1))),
    );
    const vistos = new Map<string, DiaEspecial>();
    for (const d of partes.flat()) {
        vistos.set(d.id, d);
    }
    return [...vistos.values()];
}

let temporal = 0;
const idTemporal = () => {
    temporal += 1;
    return `pendiente-${String(temporal)}`;
};
const esTemporal = (d: DiaEspecial) => d.id.startsWith('pendiente-');

const ICONO_TIPO: Readonly<Record<DiaEspecial['kind'], NombreIcono>> = { closed: 'calendar-x', special_hours: 'calendar-clock', holiday: 'flag' };

function unirNombres(nombres: readonly string[]): string {
    return nombres.length < 2 ? nombres.join('') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
}

export function Excepciones(props: Props) {
    const { ops, negocio, calendario, calNegocio, esSede, editable, hoy, zona, movil, pedir, avisar, asegurarCalendario } = props;
    const anio = anioDe(hoy);
    const [propias, setPropias] = useState<DiaEspecial[]>([]);
    const [delNegocio, setDelNegocio] = useState<DiaEspecial[]>([]);
    const [ventanas, setVentanas] = useState(1);
    const [intento, setIntento] = useState(0);
    const [festivos, setFestivos] = useState<Festivo[] | null>(null);
    const [todos, setTodos] = useState(false);
    const [ocultas, setOcultas] = useState<ReadonlySet<string>>(new Set());
    const [nuevas, setNuevas] = useState<DiaEspecial[]>([]);
    const [festivosOcultos, setFestivosOcultos] = useState<ReadonlySet<string>>(new Set());
    const [abierto, setAbierto] = useState<Abierto | null>(null);
    const [ahora] = useState(() => Date.now());

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const calId = calendario?.id ?? null;
    const negocioId = calNegocio?.id ?? null;
    useEffect(() => {
        let vivo = true;
        Promise.all([calId === null ? Promise.resolve([]) : diasEn(ops, negocio, calId, hoy, ventanas), esSede && negocioId !== null ? diasEn(ops, negocio, negocioId, hoy, ventanas) : Promise.resolve([])])
            .then(([p, n]) => {
                if (vivo) {
                    setPropias(p);
                    setDelNegocio(n);
                    setOcultas(new Set());
                    setNuevas([]);
                    setFestivosOcultos(new Set());
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, calId, negocioId, esSede, hoy, ventanas, intento]);

    const { pais, region } = props;
    useEffect(() => {
        let vivo = true;
        const pedirAnio = (y: number) => ops.listHolidayCatalog(negocio, { country: pais, year: y, ...(region === null ? {} : { region }), ...(calId === null ? {} : { calendar_id: calId }) });
        Promise.all([pedirAnio(anio), pedirAnio(anio + 1)])
            .then(([a, b]) => {
                if (vivo) {
                    setFestivos([...a.data, ...b.data]);
                }
            })
            .catch(() => {
                if (vivo) {
                    setFestivos(null);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pais, region, anio, calId, intento]);

    // ---- Lista ----
    const filas = [
        ...propias.filter((d) => !ocultas.has(d.id)).map((d) => ({ d, propia: true })),
        ...nuevas.map((d) => ({ d, propia: true })),
        ...(esSede ? delNegocio.map((d) => ({ d, propia: false })) : []),
    ]
        .filter((x) => x.d.end_date >= hoy && x.d.status === 'active')
        .sort((a, b) => (a.d.start_date < b.d.start_date ? -1 : a.d.start_date > b.d.start_date ? 1 : 0));
    const codigosPuestos = new Set([...propias, ...nuevas, ...delNegocio].filter((d) => !ocultas.has(d.id)).map((d) => d.holiday_code).filter((c): c is string => c !== undefined));
    const sugeridos = (festivos ?? []).filter((f) => f.date >= hoy && (f.exception_id == null || ocultas.has(f.exception_id)) && !codigosPuestos.has(f.holiday_code) && !festivosOcultos.has(f.holiday_code)).sort((a, b) => (a.date < b.date ? -1 : 1));
    const vistos = todos ? sugeridos : sugeridos.slice(0, 4);

    const notaPropio = (d: DiaEspecial, propia: boolean): string | null => {
        if (d.kind !== 'special_hours') {
            return null;
        }
        if (esSede && !propia && props.sedeConPropio) {
            return `${props.nombreSede} tiene horario propio: este día especial no le cambia.`;
        }
        if (!esSede && props.sedesConPropio.length > 0) {
            const n = props.sedesConPropio.length;
            return `${unirNombres(props.sedesConPropio)} ${n === 1 ? 'tiene' : 'tienen'} horario propio: este día especial no ${n === 1 ? 'le' : 'les'} cambia.`;
        }
        return null;
    };

    // ---- Panel ----
    const abrirNuevo = () => {
        setAbierto({ dia: null, b: { tipo: 'closed', desde: hoy, hasta: hoy, tramos: [{ ...props.porDefecto }], motivo: '' }, intentado: false, errores: {} });
    };
    const abrirDia = (d: DiaEspecial) => {
        setAbierto({ dia: d, b: { tipo: d.kind, desde: d.start_date, hasta: d.end_date, tramos: (d.intervals ?? []).map(rangoDeTramo), motivo: d.reason ?? '' }, intentado: false, errores: {} });
    };
    const poner = (cambios: Partial<Borrador>) => {
        setAbierto((a) => (a === null ? a : { ...a, b: { ...a.b, ...cambios }, errores: {} }));
    };
    const cerrar = () => {
        setAbierto(null);
    };

    const b = abierto?.b;
    const delCatalogo = abierto?.dia?.source === 'holiday_catalog';
    const nuevo = abierto !== null && abierto.dia === null;
    const especial = b?.tipo === 'special_hours';
    const erroresTramos = b === undefined || !especial ? [] : (erroresDeSemana([b.tramos, [], [], [], [], [], []])[0] ?? []);
    const choca = b === undefined ? undefined : propias.filter((d) => !ocultas.has(d.id) && d.id !== abierto?.dia?.id && d.status === 'active').find((d) => b.desde <= d.end_date && b.hasta >= d.start_date);
    const fallos: Record<string, string> = {};
    if (b !== undefined) {
        if (b.hasta < b.desde) {
            fallos.hasta = 'Tiene que ser el mismo día o después.';
        }
        if (b.tipo === 'holiday' && !delCatalogo && b.motivo.trim() === '') {
            fallos.motivo = 'Escribe qué fiesta es.';
        }
        if (especial && b.tramos.length === 0) {
            fallos.tramos = 'Añade al menos un tramo.';
        }
    }
    const hayFallos = Object.keys(fallos).length > 0 || erroresTramos.some((e) => e !== '') || choca !== undefined;

    /** Los 422 de la API bajo su campo (`start_date`, `end_date`, `intervals.N…`, `reason`). */
    const erroresDeApi = (e: unknown): boolean => {
        if (!(e instanceof ErrorApi) || e.errores.length === 0) {
            return false;
        }
        const r: Record<string, string> = {};
        for (const x of e.errores) {
            const tramo = /intervals(?:\.|\[)(\d+)/.exec(x.field);
            const clave = tramo !== null ? `tramo${tramo[1] ?? ''}` : x.field.includes('end_date') ? 'hasta' : x.field.includes('start_date') ? 'desde' : x.field.includes('reason') ? 'motivo' : null;
            if (clave !== null) {
                r[clave] = x.message ?? 'No es válido.';
            }
        }
        if (Object.keys(r).length === 0) {
            return false;
        }
        setAbierto((a) => (a === null ? a : { ...a, errores: r }));
        return true;
    };

    const guardar = async () => {
        if (abierto === null || b === undefined) {
            return;
        }
        if (hayFallos) {
            setAbierto({ ...abierto, intentado: true });
            return;
        }
        const cal = await asegurarCalendario();
        if (cal === null) {
            return;
        }
        const motivo = b.motivo.trim();
        const intervalos = especial ? { intervals: b.tramos.map(tramoDeRango) } : {};
        const dia: NuevoDiaEspecial = { kind: b.tipo, start_date: b.desde, end_date: b.hasta, ...intervalos, ...(motivo === '' ? {} : { reason: motivo }), ...(abierto.dia?.holiday_code === undefined ? {} : { holiday_code: abierto.dia.holiday_code }) };
        const contexto = `${NOMBRE_TIPO[b.tipo]} · ${fechasDe(b.desde, b.hasta, anio)}`;
        const temp: DiaEspecial = { ...dia, id: idTemporal(), calendar_id: cal.id, status: 'active', source: 'manual', version: 0, etag: '' };
        const anterior = abierto.dia;
        if (anterior === null) {
            await pedir({
                contexto,
                icono: ICONO_TIPO[b.tipo],
                todoElNegocio: !esSede,
                peticion: { kind: 'exception', calendar_id: cal.id, exception: dia },
                aplicar: (changeId, clave) => ops.createException(negocio, cal.id, { ...dia, ...(changeId === undefined ? {} : { change_id: changeId }) }, clave).then(() => undefined),
                textoDeshacer: `Día especial añadido: ${fechaLarga(b.desde, anio)}.`,
                alProgramar: () => {
                    setAbierto(null);
                    setNuevas((n) => [...n, temp]);
                },
                alDeshacer: () => {
                    setNuevas((n) => n.filter((x) => x.id !== temp.id));
                },
                alGuardar: () => {
                    setAbierto(null);
                    recargar();
                },
                alFallar: erroresDeApi,
            });
            return;
        }
        const cambios = { start_date: b.desde, end_date: b.hasta, ...intervalos, ...(motivo === '' ? {} : { reason: motivo }) };
        await pedir({
            contexto,
            icono: ICONO_TIPO[b.tipo],
            todoElNegocio: !esSede,
            peticion: { kind: 'exception', calendar_id: cal.id, exception_id: anterior.id, exception: { ...dia, kind: anterior.kind } },
            aplicar: (changeId, clave) => ops.updateException(negocio, cal.id, anterior.id, { ...cambios, ...(changeId === undefined ? {} : { change_id: changeId }) }, versionDe(anterior), clave).then(() => undefined),
            textoDeshacer: 'Día especial guardado.',
            alProgramar: () => {
                setAbierto(null);
                setOcultas((o) => new Set([...o, anterior.id]));
                setNuevas((n) => [...n, { ...temp, kind: anterior.kind }]);
            },
            alDeshacer: () => {
                setOcultas((o) => new Set([...o].filter((x) => x !== anterior.id)));
                setNuevas((n) => n.filter((x) => x.id !== temp.id));
            },
            alGuardar: () => {
                setAbierto(null);
                recargar();
            },
            alFallar: (e) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: TEXTO_CONFLICTO_DIA });
                    setAbierto(null);
                    recargar();
                    return true;
                }
                return erroresDeApi(e);
            },
        });
    };

    const quitar = async (d: DiaEspecial) => {
        const calDia = d.calendar_id;
        const nombre = d.reason ?? NOMBRE_TIPO[d.kind];
        await pedir({
            contexto: `Quitar el día especial · ${nombre}, ${fechasDe(d.start_date, d.end_date, anio)}`,
            icono: 'calendar-x',
            todoElNegocio: !esSede,
            peticion: { kind: 'withdraw_exception', calendar_id: calDia, exception_id: d.id },
            aplicar: (changeId, clave) => ops.withdrawException(negocio, calDia, d.id, changeId, clave),
            textoDeshacer: `Quitado: ${d.reason ?? 'día especial'}.`,
            alProgramar: () => {
                setAbierto(null);
                setOcultas((o) => new Set([...o, d.id]));
            },
            alDeshacer: () => {
                setOcultas((o) => new Set([...o].filter((x) => x !== d.id)));
            },
            alGuardar: () => {
                setAbierto(null);
                recargar();
            },
        });
    };

    const anadirFestivo = async (f: Festivo) => {
        const cal = await asegurarCalendario();
        if (cal === null) {
            return;
        }
        const dia: NuevoDiaEspecial = { kind: 'holiday', start_date: f.date, end_date: f.date, holiday_code: f.holiday_code, reason: f.name };
        const temp: DiaEspecial = { ...dia, id: idTemporal(), calendar_id: cal.id, status: 'active', source: 'holiday_catalog', version: 0, etag: '' };
        await pedir({
            contexto: `Festivo · ${f.name}, ${fechaLarga(f.date, anio)}`,
            icono: 'flag',
            todoElNegocio: !esSede,
            peticion: { kind: 'exception', calendar_id: cal.id, exception: dia },
            aplicar: (changeId, clave) => ops.createException(negocio, cal.id, { ...dia, ...(changeId === undefined ? {} : { change_id: changeId }) }, clave).then(() => undefined),
            textoDeshacer: `Cerramos el ${fechaLarga(f.date, anio)}: ${f.name}.`,
            alProgramar: () => {
                setFestivosOcultos((o) => new Set([...o, f.holiday_code]));
                setNuevas((n) => [...n, temp]);
            },
            alDeshacer: () => {
                setFestivosOcultos((o) => new Set([...o].filter((x) => x !== f.holiday_code)));
                setNuevas((n) => n.filter((x) => x.id !== temp.id));
            },
            alGuardar: recargar,
        });
    };

    // ---- Cierres ----
    const hayAhora = props.cierres.some((c) => c.status === 'active' && Date.parse(c.starts_at) <= ahora && ahora < Date.parse(c.ends_at));
    const nombreDe = (id: string) => props.reservables.find((r) => r.id === id)?.name;
    const cuandoCierre = (c: Cierre) => {
        const s = enZona(c.starts_at, zona);
        const e = enZona(c.ends_at, zona);
        const dia = (f: string) => (f === hoy ? 'Hoy' : fechaCorta(f, anio));
        const tramo = s.fecha === e.fecha ? `${dia(s.fecha)}, de ${s.hora} a ${e.hora}` : `Del ${dia(s.fecha)} ${s.hora} al ${dia(e.fecha).toLocaleLowerCase('es')} ${e.hora}`;
        const reabierto = c.reopened_at === undefined ? '' : ` · reabierto el ${fechaCorta(enZona(c.reopened_at, zona).fecha, anio)}`;
        const caduca = c.expires_at === undefined || c.status !== 'active' ? '' : ` · caduca a las ${enZona(c.expires_at, zona).hora}`;
        return `${tramo}${reabierto}${caduca}`;
    };

    // ---- Panel abierto ----
    const errorDe = (k: string) => abierto?.errores[k] ?? (abierto?.intentado === true ? fallos[k] : undefined);
    const panelTitulo = abierto === null ? '' : nuevo ? 'Nuevo día especial' : (abierto.dia?.reason ?? NOMBRE_TIPO[abierto.b.tipo]);
    const panelEyebrow = abierto === null ? '' : nuevo ? 'Día especial' : delCatalogo ? 'Festivo oficial' : NOMBRE_TIPO[abierto.b.tipo];
    const subtitulo = b === undefined ? '' : b.desde === b.hasta ? fechaLarga(b.desde, anio).replace(/^./, (c) => c.toLocaleUpperCase('es')) : `Del ${fechaLarga(b.desde, anio)} al ${fechaLarga(b.hasta, anio)}`;
    const abreCerrado = b !== undefined && especial && b.desde === b.hasta && (props.semana[diaDeLaSemana(b.desde)] ?? []).length === 0;
    const puedeEditarPanel = editable && abierto?.dia !== undefined && (abierto.dia === null || !esTemporal(abierto.dia));

    return (
        <section id="rv-excepciones" className="rv-hor-exc" aria-labelledby="rv-excepciones-titulo">
            <div className="rv-hor-exc__cab">
                <div>
                    <h2 id="rv-excepciones-titulo">Excepciones y festivos</h2>
                    <p>Los días que cierras o abres con otro horario. Mandan sobre la semana.</p>
                </div>
                {editable && (
                    <Boton
                        tipo="secundario"
                        icono="plus"
                        onClick={() => {
                            abrirNuevo();
                        }}
                    >
                        Añadir día especial
                    </Boton>
                )}
            </div>
            <div className="rv-hor-exc__cols">
                <div className="rv-hor-exc__principal">
                    <span className="rv-hor-exc__rango">{ventanas === 1 ? 'Próximos 3 meses' : `Próximos ${String(ventanas * 3)} meses`}</span>
                    {filas.length > 0 ? (
                        <div role="list" aria-label="Próximos días especiales" className="rv-hor-lista">
                            {filas.map(({ d, propia }) => {
                                const t = baldosa(d.start_date);
                                const nota = notaPropio(d, propia);
                                const abre = propia && !esTemporal(d);
                                const titulo = d.reason ?? NOMBRE_TIPO[d.kind];
                                return (
                                    <div key={d.id} role="listitem">
                                        <button
                                            type="button"
                                            className={`rv-hor-fila${propia ? '' : ' rv-hor-fila--ajena'}`}
                                            disabled={!abre}
                                            aria-label={`${titulo}, ${lineaDia(d, anio)}${propia ? '' : ', del negocio'}`}
                                            onClick={() => {
                                                abrirDia(d);
                                            }}
                                        >
                                            <span className={`rv-hor-baldosa${d.kind === 'special_hours' ? ' rv-hor-baldosa--negra' : ''}`}>
                                                <strong>{t.dia}</strong>
                                                <span>{t.mes}</span>
                                            </span>
                                            <span className="rv-hor-fila__texto">
                                                <strong>{titulo}</strong>
                                                <span>{`${lineaDia(d, anio)}${propia ? '' : ' · del negocio'}`}</span>
                                                {nota !== null && <span className="rv-hor-fila__nota">{nota}</span>}
                                            </span>
                                            {abre && <Icono nombre="chevron-right" className="rv-ca-chevron" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa">
                                <Icono nombre="calendar-check" />
                            </span>
                            <strong>Ningún día especial a la vista</strong>
                            <span>Todos los días siguen el horario de arriba. Añade un cierre, un horario especial o un festivo.</span>
                        </div>
                    )}
                    <Boton
                        tipo="fantasma"
                        icono="chevron-down"
                        className="rv-hor-mas"
                        onClick={() => {
                            setVentanas((v) => v + 1);
                        }}
                    >
                        Ver más adelante
                    </Boton>
                    {esSede && filas.some((f) => !f.propia) && (
                        <p className="rv-ca-nota">
                            <Icono nombre="info" />
                            <span>Los días especiales de todo el negocio también salen aquí. Se cambian eligiendo «Todo el negocio».</span>
                        </p>
                    )}
                    {props.cierres.length > 0 && (
                        <details className="rv-hor-plegable">
                            <summary>
                                <span className="rv-hor-plegable__baldosa">
                                    <Icono nombre="lock" />
                                </span>
                                <span className="rv-hor-plegable__texto">
                                    <strong>Cierres y retenes</strong>
                                    <span>{`${hayAhora ? 'Hay cierres ahora' : 'Nada cerrado ahora'} · se hacen en Cierre de recursos y en Sala`}</span>
                                </span>
                                <Icono nombre="chevron-down" />
                            </summary>
                            <div role="list" aria-label="Cierres y retenes" className="rv-hor-plegable__cuerpo">
                                <p className="rv-ca-nota">
                                    <Icono nombre="info" />
                                    <span>Los cierres se hacen en Cierre de recursos y los retenes en Sala. Aquí solo se consultan.</span>
                                </p>
                                {props.cierres.map((c) => {
                                    const reten = c.scope === 'remote_channels';
                                    const nombres = c.bookable_ids.map(nombreDe).filter((n): n is string => n !== undefined && n !== '');
                                    const estado = ESTADO_CIERRE[c.status];
                                    return (
                                        <div key={c.id} role="listitem" className="rv-hor-cierre">
                                            <span className="rv-ca-baldosa">
                                                <Icono nombre={MOTIVO_CIERRE[c.reason_kind].icono} />
                                            </span>
                                            <span className="rv-ca-fila__texto">
                                                <strong>{`${nombres.length > 0 ? nombres.join(', ') : 'Sin nombre'} · ${reten ? 'Retén de puerta' : MOTIVO_CIERRE[c.reason_kind].texto}`}</strong>
                                                <span>{`${cuandoCierre(c)} · ${reten ? 'solo web, widget, chat y API' : 'todos los canales'}${c.reason === undefined || c.reason === '' ? '' : ` · ${c.reason}`}`}</span>
                                            </span>
                                            <Chip estado={estado.estado}>{estado.texto}</Chip>
                                        </div>
                                    );
                                })}
                            </div>
                        </details>
                    )}
                </div>
                {festivos !== null && (
                    <aside className="rv-hor-festivos" aria-label="Festivos que puedes añadir">
                        <div className="rv-hor-festivos__cab">
                            <span className="rv-ca-baldosa">
                                <Icono nombre="flag" />
                            </span>
                            <span className="rv-ca-fila__texto">
                                <strong>Festivos que puedes añadir</strong>
                                <span>{props.nombreRegion === null ? props.nombrePais : `${props.nombrePais} · ${props.nombreRegion}`}</span>
                            </span>
                        </div>
                        <p className="rv-hor-festivos__texto">Ninguno se aplica solo: muchos negocios abren en festivo. Añade los que cierras.</p>
                        {props.comunidadesMezcladas === true && !props.esSede && (
                            <p className="rv-hor-festivos__texto">
                                Tus sedes no tienen todas la misma comunidad: aquí solo salen los nacionales. Los de cada comunidad se añaden en el horario de cada sede. <em>Texto a validar</em>
                            </p>
                        )}
                        {vistos.map((f) => {
                            const t = baldosa(f.date);
                            const ambito = f.scope === 'national' ? 'Nacional' : (props.nombreRegion ?? 'Autonómico');
                            const y = anioDe(f.date);
                            return (
                                <div key={f.holiday_code} className="rv-hor-festivo">
                                    <span className="rv-hor-baldosa rv-hor-baldosa--clara">
                                        <strong>{t.dia}</strong>
                                        <span>{t.mes}</span>
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <strong>{f.name}</strong>
                                        <span>{`${ambito} · ${DIAS[diaDeLaSemana(f.date)] ?? ''}${y === anio ? '' : ` · ${String(y)}`}`}</span>
                                    </span>
                                    {editable && (
                                        <Boton
                                            tipo="secundario"
                                            icono="plus"
                                            aria-label={`Añadir ${f.name}, ${fechaLarga(f.date, anio)}, como día cerrado`}
                                            onClick={() => {
                                                void anadirFestivo(f);
                                            }}
                                        >
                                            Añadir
                                        </Boton>
                                    )}
                                </div>
                            );
                        })}
                        {sugeridos.length === 0 && <p className="rv-hor-festivos__texto">Ya has añadido todos los de este año.</p>}
                        {sugeridos.length > 4 && (
                            <button
                                type="button"
                                className="rv-hor-enlace"
                                aria-expanded={todos}
                                onClick={() => {
                                    setTodos((x) => !x);
                                }}
                            >
                                {todos ? 'Ver menos' : 'Ver todos los festivos'}
                            </button>
                        )}
                    </aside>
                )}
            </div>

            <PanelLateral
                abierto={abierto !== null}
                onCerrar={cerrar}
                movil={movil}
                titulo={panelTitulo}
                eyebrow={panelEyebrow}
                subtitulo={subtitulo}
                icono={b === undefined ? 'calendar-x' : ICONO_TIPO[b.tipo]}
                accion={puedeEditarPanel && !delCatalogo ? (nuevo ? 'Añadir' : 'Guardar') : ''}
                ocupado={props.calculando}
                ocupadoTexto="Calculando…"
                onAccion={() => {
                    void guardar();
                }}
            >
                {abierto !== null && b !== undefined && (
                    <div className="rv-neg-campos">
                        {delCatalogo && (
                            <p className="rv-ca-nota">
                                <Icono nombre="flag" />
                                <span>Festivo oficial elegido de la lista. Para dejar de cerrarlo, quítalo aquí.</span>
                            </p>
                        )}
                        {nuevo && (
                            <div className="rv-hor-tipos">
                                <strong>Qué pasa ese día</strong>
                                <div role="radiogroup" aria-label="Tipo de día especial" className="rv-hor-tipos__lista">
                                    {TIPOS_DIA.map((t) => (
                                        <OpcionRadio
                                            key={t.tipo}
                                            elegida={b.tipo === t.tipo}
                                            icono={t.icono}
                                            titulo={t.nombre}
                                            texto={t.ejemplo}
                                            bloqueada={!editable}
                                            onElegir={() => {
                                                poner({ tipo: t.tipo });
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="rv-neg-dos">
                            <Fecha
                                etiqueta="Desde"
                                valor={b.desde}
                                bloqueado={!puedeEditarPanel || delCatalogo}
                                error={errorDe('desde')}
                                onChange={(v) => {
                                    if (v !== '') {
                                        poner({ desde: v, hasta: b.hasta < v ? v : b.hasta });
                                    }
                                }}
                            />
                            <Fecha
                                etiqueta="Hasta"
                                valor={b.hasta}
                                min={b.desde}
                                bloqueado={!puedeEditarPanel || delCatalogo}
                                error={errorDe('hasta')}
                                onChange={(v) => {
                                    if (v !== '') {
                                        poner({ hasta: v });
                                    }
                                }}
                            />
                        </div>
                        {especial && (
                            <div className="rv-hor-tipos">
                                <strong>Ese día abre</strong>
                                {b.tramos.map((t, i) => {
                                    const ponerTramo = (r: Rango) => {
                                        poner({ tramos: b.tramos.map((x, j) => (j === i ? r : x)) });
                                    };
                                    const escribir = (fn: typeof desdeEscrito) => (texto: string) => {
                                        const r = fn(texto, t);
                                        if (typeof r === 'string') {
                                            return r;
                                        }
                                        ponerTramo(r);
                                        return '';
                                    };
                                    const error = abierto.errores[`tramo${String(i)}`] ?? erroresTramos[i] ?? '';
                                    return (
                                        <div key={i} className="rv-hor-tramo">
                                            <Deslizador
                                                etiqueta={`Horario especial${b.tramos.length > 1 ? `, tramo ${String(i + 1)}` : ''}`}
                                                desde={t.desde}
                                                hasta={t.hasta}
                                                min={DESLIZADOR.min}
                                                max={DESLIZADOR.max}
                                                paso={DESLIZADOR.paso}
                                                maxDist={DURACION_MAXIMA}
                                                marcas={MARCAS}
                                                desactivado={!puedeEditarPanel}
                                                error={error}
                                                onRango={(desde, hasta) => {
                                                    ponerTramo(ajustarRango({ desde, hasta }));
                                                }}
                                                {...(puedeEditarPanel ? { escribirDesde: escribir(desdeEscrito), escribirHasta: escribir(hastaEscrito) } : {})}
                                            />
                                            <div className="rv-hor-tramo__nota">
                                                <span>
                                                    <Icono nombre={t.hasta > 1440 ? 'moon' : 'clock'} />
                                                    {t.hasta > 1440 ? `Termina al día siguiente · ${textoDuracion(t.hasta - t.desde)}` : `Dura ${textoDuracion(t.hasta - t.desde)}`}
                                                </span>
                                                {puedeEditarPanel && b.tramos.length > 1 && (
                                                    <Boton
                                                        tipo="secundario"
                                                        rojo
                                                        icono="x"
                                                        aria-label={`Quitar el tramo ${String(i + 1)}`}
                                                        onClick={() => {
                                                            poner({ tramos: b.tramos.filter((_, j) => j !== i) });
                                                        }}
                                                    >
                                                        Quitar
                                                    </Boton>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {errorDe('tramos') !== undefined && (
                                    <span role="alert" className="rv-desl__error">
                                        <Icono nombre="circle-alert" />
                                        <span>{errorDe('tramos')}</span>
                                    </span>
                                )}
                                {puedeEditarPanel && b.tramos.length < 10 && (
                                    <Boton
                                        tipo="secundario"
                                        icono="plus"
                                        onClick={() => {
                                            poner({ tramos: [...b.tramos, tramoNuevo(b.tramos, props.porDefecto)] });
                                        }}
                                    >
                                        Añadir tramo
                                    </Boton>
                                )}
                            </div>
                        )}
                        {abreCerrado && (
                            <p role="status" className="rv-ca-nota">
                                <Icono nombre="door-open" />
                                <span>{`El ${DIAS[diaDeLaSemana(b.desde)] ?? ''} cierras: con este horario especial abres ese día y se puede reservar por todos los canales.`}</span>
                            </p>
                        )}
                        {choca !== undefined && (
                            <p role="alert" className="rv-hor-alerta">
                                <Icono nombre="circle-alert" />
                                <span>{`Ya hay un día especial el ${fechaLarga(choca.start_date, anio)} (${choca.reason ?? 'sin motivo'}). Cámbialo o quítalo antes.`}</span>
                            </p>
                        )}
                        {!delCatalogo && (
                            <Campo
                                etiqueta={b.tipo === 'holiday' ? 'Motivo' : 'Motivo (opcional)'}
                                valor={b.motivo}
                                onChange={(v) => {
                                    poner({ motivo: v });
                                }}
                                bloqueado={!puedeEditarPanel}
                                max={140}
                                mayusculas="sentences"
                                autocompletar="off"
                                error={errorDe('motivo')}
                                ayuda="Lo ve tu equipo: obras, Nochevieja, inventario…"
                            />
                        )}
                        {puedeEditarPanel && abierto.dia !== null && (
                            <div className="rv-neg-archivar">
                                <Boton
                                    tipo="secundario"
                                    rojo
                                    icono="x"
                                    onClick={() => {
                                        if (abierto.dia !== null) {
                                            void quitar(abierto.dia);
                                        }
                                    }}
                                >
                                    Quitar este día especial
                                </Boton>
                                <span className="rv-hor-tramo__nota">Vuelve a regir la semana tipo. Tienes 5 segundos para deshacerlo.</span>
                            </div>
                        )}
                    </div>
                )}
            </PanelLateral>
        </section>
    );
}

